import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  setDoc,
  updateDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp,
  arrayUnion,
  arrayRemove,
  onSnapshot,
  deleteField
} from 'firebase/firestore';
import { db } from '../config/firebase';

const COLLECTIONS = {
  LEADS: 'leads',
  EMPLOYEES: 'employees',
  CALL_LOGS: 'callLogs',
  CHAT_DATA: 'ChatData',
  INDIVIDUAL_LEADS: 'individualLeads',
  CHALLENGE_MODE: 'challengeMode'
};

/**
 * Get employee by email
 */
export const getEmployeeByEmail = async (email) => {
  try {
    const employeesRef = collection(db, COLLECTIONS.EMPLOYEES);
    const q = query(employeesRef, where('email', '==', email));
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      const doc = querySnapshot.docs[0];
      return {
        _id: doc.id,
        ...doc.data()
      };
    }
    return null;
  } catch (error) {
    console.error('Error fetching employee:', error);
    throw error;
  }
};

/**
 * Get employee by ID
 */
export const getEmployeeById = async (employeeId) => {
  try {
    const employeeRef = doc(db, COLLECTIONS.EMPLOYEES, employeeId);
    const docSnap = await getDoc(employeeRef);
    
    if (docSnap.exists()) {
      return {
        _id: docSnap.id,
        ...docSnap.data()
      };
    }
    return null;
  } catch (error) {
    console.error('Error fetching employee:', error);
    throw error;
  }
};

/**
 * Update employee status (online/offline)
 * @param {string} employeeId - The employee ID
 * @param {string} status - Status: 'online' or 'offline'
 */
export const updateEmployeeStatus = async (employeeId, status) => {
  try {
    if (!employeeId) {
      throw new Error('Employee ID is required');
    }

    const employeeRef = doc(db, COLLECTIONS.EMPLOYEES, employeeId);
    await updateDoc(employeeRef, {
      status: status,
      updatedAt: serverTimestamp()
    });
    
    return true;
  } catch (error) {
    console.error('Error updating employee status:', error);
    throw error;
  }
};

/**
 * Subscribe to real-time employee status updates
 * @param {string} employeeId - The employee ID
 * @param {function} callback - Callback function that receives the employee data
 * @returns {function} Unsubscribe function to stop listening
 */
export const subscribeToEmployeeStatus = (employeeId, callback) => {
  try {
    if (!employeeId) {
      console.warn('Employee ID is required to subscribe to employee status');
      return () => {}; // Return empty unsubscribe function
    }

    const employeeRef = doc(db, COLLECTIONS.EMPLOYEES, employeeId);
    
    const unsubscribe = onSnapshot(employeeRef, (docSnapshot) => {
      if (docSnapshot.exists()) {
        const data = docSnapshot.data();
        const employeeData = {
          _id: docSnapshot.id,
          ...data
        };
        callback(employeeData);
      } else {
        callback(null);
      }
    }, (error) => {
      console.error('Error in employee status subscription:', error);
      callback(null);
    });
    
    return unsubscribe;
  } catch (error) {
    console.error('Error setting up employee status subscription:', error);
    return () => {}; // Return empty unsubscribe function
  }
};

/**
 * Get leads assigned to an employee
 */
/**
 * Format date as DD-MM-YYYY
 */
const formatDate = (date) => {
  const d = date instanceof Date ? date : new Date(date);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
};

/**
 * Parse date from DD-MM-YYYY format to Date object
 */
const parseDate = (dateStr) => {
  try {
    const [day, month, year] = dateStr.split('-');
    if (!day || !month || !year) {
      console.warn('Invalid date format:', dateStr);
      return new Date();
    }
    // Create date in YYYY-MM-DD format (ISO format)
    const date = new Date(`${year}-${month}-${day}`);
    // Validate the date
    if (isNaN(date.getTime())) {
      console.warn('Invalid date parsed:', dateStr);
      return new Date();
    }
    return date;
  } catch (error) {
    console.error('Error parsing date:', dateStr, error);
    return new Date();
  }
};

/**
 * Get employee leads from individualLeads collection
 * Structure: individualLeads/{agentName}/{date}/{phoneNumber}/{clientData}
 */
export const getEmployeeLeads = async (employeeId, employeeName) => {
  try {
    if (!employeeName) {
      console.warn('Employee name is required');
      return [];
    }
    
    // Get the document for this employee (agentName)
    const individualLeadsRef = doc(db, COLLECTIONS.INDIVIDUAL_LEADS, employeeName);
    const docSnap = await getDoc(individualLeadsRef);
    
    if (!docSnap.exists()) {
      console.log('No individual leads found for:', employeeName);
      return [];
    }
    
    const agentData = docSnap.data();
    const leads = [];
    
    // Iterate through all dates in the document
    Object.keys(agentData).forEach((date) => {
      // Skip metadata fields
      if (date === 'updatedAt' || date === 'createdAt') return;
      
      const dateData = agentData[date];
      if (!dateData || typeof dateData !== 'object') return;
      
      // Get all phone numbers for this date
      const phoneNumbers = Object.keys(dateData);
      
      if (phoneNumbers.length > 0) {
        // Parse date from DD-MM-YYYY format
        const createdAt = parseDate(date);
        
        // Build phone statuses and client data
        const phoneStatuses = {};
        const phoneNames = {};
        const phoneClientData = {};
        const leadData = [];
        
        phoneNumbers.forEach(phone => {
          const clientData = dateData[phone];
          if (!clientData || typeof clientData !== 'object') return;
          
          const status = clientData.status || 'new';
          const clientName = clientData.clientName || '';
          
          phoneStatuses[phone] = status;
          phoneNames[phone] = clientName;
          phoneClientData[phone] = {
            clientName: clientName,
            clientEmail: clientData.clientEmail || '',
            clientAddress: clientData.clientAddress || '',
            status: status,
            remarks: clientData.remarks || ''
          };
          
          // Add to leadData array in the format expected by Dashboard
          leadData.push(`contactNumber:${phone}`);
        });
        
        // Get remarks (use first phone's remarks or combine)
        const remarks = phoneNumbers.length > 0 && dateData[phoneNumbers[0]]?.remarks 
          ? dateData[phoneNumbers[0]].remarks 
          : '';
        
        // Create a lead entry for this date
        leads.push({
          _id: `${employeeName}_${date}`,
          leadName: `${employeeName} - ${date}`,
          leadData: leadData,
          assignedTo: employeeName,
          status: 'new', // Default status (individual phones have their own status)
          phoneStatuses: phoneStatuses,
          phoneNames: phoneNames,
          phoneClientData: phoneClientData,
          createdBy: employeeName,
          remarks: remarks,
          createdAt: createdAt,
          updatedAt: agentData.updatedAt?.toDate ? agentData.updatedAt.toDate() : createdAt,
          date: date // Store the date string for updates
        });
      }
    });
    
    // Sort by createdAt descending (most recent first)
    leads.sort((a, b) => {
      const dateA = a.createdAt instanceof Date ? a.createdAt : new Date(a.createdAt);
      const dateB = b.createdAt instanceof Date ? b.createdAt : new Date(b.createdAt);
      return dateB - dateA;
    });
    
    return leads;
  } catch (error) {
    console.error('Error fetching employee leads from individualLeads:', error);
    return [];
  }
};

/**
 * Update lead status in individualLeads collection
 * leadId format: {agentName}_{date}
 */
export const updateLeadStatus = async (leadId, status, remarks = '') => {
  try {
    // Parse leadId to get agentName and date
    const parts = leadId.split('_');
    if (parts.length < 2) {
      throw new Error('Invalid leadId format. Expected: {agentName}_{date}');
    }
    
    const date = parts.slice(1).join('_'); // In case date has underscores
    const agentName = parts[0];
    
    const individualLeadsRef = doc(db, COLLECTIONS.INDIVIDUAL_LEADS, agentName);
    const docSnap = await getDoc(individualLeadsRef);
    
    if (!docSnap.exists()) {
      throw new Error('Lead document not found');
    }
    
    const existingData = docSnap.data();
    const dateData = existingData[date] || {};
    
    // Update status for all phone numbers in this date
    const updatedDateData = { ...dateData };
    Object.keys(updatedDateData).forEach(phone => {
      if (updatedDateData[phone] && typeof updatedDateData[phone] === 'object') {
        updatedDateData[phone] = {
          ...updatedDateData[phone],
          status: status,
          remarks: remarks || updatedDateData[phone].remarks || ''
        };
      }
    });
    
    await setDoc(individualLeadsRef, {
      ...existingData,
      [date]: updatedDateData,
      updatedAt: serverTimestamp()
    }, { merge: true });
    
    return true;
  } catch (error) {
    console.error('Error updating lead status:', error);
    throw error;
  }
};

/**
 * Update phone number specific status in individualLeads collection
 * leadId format: {agentName}_{date}
 */
export const updatePhoneStatus = async (leadId, phoneNumber, status) => {
  try {
    // Parse leadId to get agentName and date
    const parts = leadId.split('_');
    if (parts.length < 2) {
      throw new Error('Invalid leadId format. Expected: {agentName}_{date}');
    }
    
    const date = parts.slice(1).join('_'); // In case date has underscores
    const agentName = parts[0];
    
    const individualLeadsRef = doc(db, COLLECTIONS.INDIVIDUAL_LEADS, agentName);
    const docSnap = await getDoc(individualLeadsRef);
    
    if (!docSnap.exists()) {
      throw new Error('Lead document not found');
    }
    
    const existingData = docSnap.data();
    const dateData = existingData[date] || {};
    
    // Update status for the specific phone number
    if (dateData[phoneNumber] && typeof dateData[phoneNumber] === 'object') {
      dateData[phoneNumber] = {
        ...dateData[phoneNumber],
        status: status
      };
    } else {
      // If phone number doesn't exist, create it
      dateData[phoneNumber] = {
        clientName: '',
        clientEmail: '',
        clientAddress: '',
        status: status,
        remarks: ''
      };
    }
    
    await setDoc(individualLeadsRef, {
      ...existingData,
      [date]: dateData,
      updatedAt: serverTimestamp()
    }, { merge: true });
    
    return true;
  } catch (error) {
    console.error('Error updating phone status:', error);
    throw error;
  }
};

/**
 * Update lead with contact information in individualLeads collection
 * leadId format: {agentName}_{date}
 * If phoneNumber is provided, updates that specific phone's data
 */
export const updateLeadContact = async (leadId, contactData, phoneNumber = null) => {
  try {
    // Parse leadId to get agentName and date
    const parts = leadId.split('_');
    if (parts.length < 2) {
      throw new Error('Invalid leadId format. Expected: {agentName}_{date}');
    }
    
    const date = parts.slice(1).join('_'); // In case date has underscores
    const agentName = parts[0];
    
    const individualLeadsRef = doc(db, COLLECTIONS.INDIVIDUAL_LEADS, agentName);
    const docSnap = await getDoc(individualLeadsRef);
    
    if (!docSnap.exists()) {
      throw new Error('Lead document not found');
    }
    
    const existingData = docSnap.data();
    const dateData = existingData[date] || {};
    
    if (phoneNumber) {
      // Update specific phone number's data
      if (dateData[phoneNumber] && typeof dateData[phoneNumber] === 'object') {
        dateData[phoneNumber] = {
          ...dateData[phoneNumber],
          clientName: contactData.clientName !== undefined ? contactData.clientName : dateData[phoneNumber].clientName,
          clientEmail: contactData.clientEmail !== undefined ? contactData.clientEmail : dateData[phoneNumber].clientEmail,
          clientAddress: contactData.clientAddress !== undefined ? contactData.clientAddress : dateData[phoneNumber].clientAddress,
          remarks: contactData.remarks !== undefined ? contactData.remarks : dateData[phoneNumber].remarks,
          status: contactData.status !== undefined ? contactData.status : dateData[phoneNumber].status
        };
      } else {
        // Create new entry for this phone number
        dateData[phoneNumber] = {
          clientName: contactData.clientName || '',
          clientEmail: contactData.clientEmail || '',
          clientAddress: contactData.clientAddress || '',
          status: contactData.status || 'new',
          remarks: contactData.remarks || ''
        };
      }
    } else {
      // Update all phone numbers in this date with the contact data
      Object.keys(dateData).forEach(phone => {
        if (dateData[phone] && typeof dateData[phone] === 'object') {
          dateData[phone] = {
            ...dateData[phone],
            remarks: contactData.remarks !== undefined ? contactData.remarks : dateData[phone].remarks
          };
        }
      });
    }
    
    await setDoc(individualLeadsRef, {
      ...existingData,
      [date]: dateData,
      updatedAt: serverTimestamp()
    }, { merge: true });
    
    return true;
  } catch (error) {
    console.error('Error updating lead contact:', error);
    throw error;
  }
};

/**
 * Log a call
 */
export const logCall = async (callData) => {
  try {
    const callLogsRef = collection(db, COLLECTIONS.CALL_LOGS);
    const callLog = {
      leadId: callData.leadId,
      employeeId: callData.employeeId,
      employeeName: callData.employeeName,
      phoneNumber: callData.phoneNumber,
      callDuration: callData.callDuration || 0,
      callStatus: callData.callStatus || 'completed', // completed, missed, no_answer
      notes: callData.notes || '',
      callStartTime: callData.callStartTime ? (callData.callStartTime instanceof Date ? callData.callStartTime : new Date(callData.callStartTime)) : null,
      callDate: serverTimestamp(),
      createdAt: serverTimestamp()
    };
    
    const docRef = await addDoc(callLogsRef, callLog);
    
    // Update lead with last call date in individualLeads collection
    if (callData.leadId && callData.phoneNumber) {
      try {
        // Parse leadId to get agentName and date
        const parts = callData.leadId.split('_');
        if (parts.length >= 2) {
          const date = parts.slice(1).join('_');
          const agentName = parts[0];
          
          const individualLeadsRef = doc(db, COLLECTIONS.INDIVIDUAL_LEADS, agentName);
          const docSnap = await getDoc(individualLeadsRef);
          
          if (docSnap.exists()) {
            const existingData = docSnap.data();
            const dateData = existingData[date] || {};
            
            // Update the specific phone number's data
            if (dateData[callData.phoneNumber] && typeof dateData[callData.phoneNumber] === 'object') {
              dateData[callData.phoneNumber] = {
                ...dateData[callData.phoneNumber],
                lastCallDate: serverTimestamp()
              };
              
              await setDoc(individualLeadsRef, {
                ...existingData,
                [date]: dateData,
                updatedAt: serverTimestamp()
              }, { merge: true });
            }
          }
        }
      } catch (updateError) {
        // Don't fail the call log if lead update fails
        console.error('Error updating lead with last call date:', updateError);
      }
    }
    
    return {
      _id: docRef.id,
      ...callLog
    };
  } catch (error) {
    console.error('Error logging call:', error);
    throw error;
  }
};

/**
 * Get call logs for a lead
 */
export const getCallLogs = async (leadId) => {
  try {
    const callLogsRef = collection(db, COLLECTIONS.CALL_LOGS);
    
    // Try query with orderBy first, fallback to just where if it fails (missing index)
    let querySnapshot;
    try {
      const q = query(
        callLogsRef,
        where('leadId', '==', leadId),
        orderBy('callDate', 'desc')
      );
      querySnapshot = await getDocs(q);
    } catch (orderByError) {
      // If orderBy fails (likely due to missing index), try without it
      console.warn('OrderBy query failed for call logs, trying without orderBy:', orderByError);
      const q = query(
        callLogsRef,
        where('leadId', '==', leadId)
      );
      querySnapshot = await getDocs(q);
    }
    
    const logs = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      logs.push({
        _id: doc.id,
        ...data,
        callDate: data.callDate?.toDate ? data.callDate.toDate() : (data.callDate || new Date()),
        createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : (data.createdAt || new Date())
      });
    });
    
    // Sort by callDate manually if orderBy didn't work
    logs.sort((a, b) => {
      const dateA = a.callDate instanceof Date ? a.callDate : new Date(a.callDate);
      const dateB = b.callDate instanceof Date ? b.callDate : new Date(b.callDate);
      return dateB - dateA; // Descending order
    });
    
    return logs;
  } catch (error) {
    console.error('Error fetching call logs:', error);
    // Return empty array instead of throwing to prevent UI crash
    return [];
  }
};

/**
 * Convert old array format loginHistory to new date-wise object format
 */
const convertLoginHistoryToDateWise = (loginHistory) => {
  if (!loginHistory) return {};
  
  // If it's already an object (new format), return as is
  if (typeof loginHistory === 'object' && !Array.isArray(loginHistory)) {
    return loginHistory;
  }
  
  // If it's an array (old format), convert to date-wise object
  if (Array.isArray(loginHistory)) {
    const dateWiseHistory = {};
    loginHistory.forEach((entry) => {
      let dateKey;
      
      // Extract date from entry
      if (entry.date) {
        dateKey = entry.date;
      } else if (entry.loginTime) {
        // Try to get date from loginTime
        const loginDate = entry.loginTime.toDate 
          ? entry.loginTime.toDate() 
          : new Date(entry.loginTime);
        dateKey = loginDate.toISOString().split('T')[0];
      } else {
        // Fallback to today's date
        dateKey = new Date().toISOString().split('T')[0];
      }
      
      // Initialize date array if it doesn't exist
      if (!dateWiseHistory[dateKey]) {
        dateWiseHistory[dateKey] = [];
      }
      
      // Add entry to the date array
      dateWiseHistory[dateKey].push({
        loginTime: entry.loginTime,
        logoutTime: entry.logoutTime || null
      });
    });
    
    return dateWiseHistory;
  }
  
  return {};
};

/**
 * Log employee login time in employee document (date-wise)
 * Directly stores login/logout details in loginHistory object
 */
export const logLoginTime = async (employeeId) => {
  try {
    const employeeRef = doc(db, COLLECTIONS.EMPLOYEES, employeeId);
    const loginTime = serverTimestamp();
    const today = new Date().toISOString().split('T')[0]; // Format: YYYY-MM-DD
    
    // Get current employee data
    const employeeDoc = await getDoc(employeeRef);
    const currentData = employeeDoc.data() || {};
    
    // Get or initialize loginHistory object
    let loginHistory = currentData.loginHistory || {};
    
    // Convert old array format to object format if needed
    if (Array.isArray(loginHistory)) {
      loginHistory = convertLoginHistoryToDateWise(loginHistory);
    }
    
    // Ensure loginHistory is an object
    if (typeof loginHistory !== 'object' || Array.isArray(loginHistory)) {
      loginHistory = {};
    }
    
    // Initialize today's array if it doesn't exist
    if (!loginHistory[today]) {
      loginHistory[today] = [];
    }
    
    // Ensure today's entry is an array
    if (!Array.isArray(loginHistory[today])) {
      loginHistory[today] = [];
    }
    
    // Directly add new login entry to today's chain in loginHistory
    loginHistory[today].push({
      loginTime: loginTime,
      logoutTime: null
    });
    
    // Directly update loginHistory in employee document
    await updateDoc(employeeRef, {
      lastLoginTime: loginTime,
      lastLogoutTime: null,
      loginHistory: loginHistory, // Directly store the updated loginHistory object
      isCurrentlyLoggedIn: true,
      updatedAt: serverTimestamp()
    });
    
    return {
      loginTime: loginTime,
      loginHistory: loginHistory
    };
  } catch (error) {
    console.error('Error logging login time:', error);
    throw error;
  }
};

/**
 * Log employee logout time in employee document (date-wise)
 * Directly updates logout details in loginHistory object
 */
export const logLogoutTime = async (employeeId, logoutTime = null) => {
  try {
    const employeeRef = doc(db, COLLECTIONS.EMPLOYEES, employeeId);
    const logoutTimestamp = logoutTime || serverTimestamp();
    const today = new Date().toISOString().split('T')[0]; // Format: YYYY-MM-DD
    
    // Get current employee data
    const employeeDoc = await getDoc(employeeRef);
    const currentData = employeeDoc.data() || {};
    
    // Get or initialize loginHistory object
    let loginHistory = currentData.loginHistory || {};
    
    // Convert old array format to object format if needed
    if (Array.isArray(loginHistory)) {
      loginHistory = convertLoginHistoryToDateWise(loginHistory);
    }
    
    // Ensure loginHistory is an object
    if (typeof loginHistory !== 'object' || Array.isArray(loginHistory)) {
      loginHistory = {};
    }
    
    // First, try to find entry in today's chain (most common case for same-day login/logout)
    let foundEntry = false;
    
    if (loginHistory[today] && Array.isArray(loginHistory[today]) && loginHistory[today].length > 0) {
      // Check today's entries in reverse order (most recent first)
      const todayEntries = loginHistory[today];
      for (let i = todayEntries.length - 1; i >= 0; i--) {
        if (todayEntries[i] && !todayEntries[i].logoutTime) {
          // Directly update logoutTime in loginHistory object
          todayEntries[i].logoutTime = logoutTimestamp;
          foundEntry = true;
          break;
        }
      }
    }
    
    // If not found in today, search other dates (for cross-day scenarios)
    if (!foundEntry) {
      const dates = Object.keys(loginHistory).sort().reverse(); // Sort dates descending
      
      for (const dateKey of dates) {
        if (dateKey === today) continue; // Already checked today
        
        const entries = loginHistory[dateKey];
        if (entries && Array.isArray(entries) && entries.length > 0) {
          // Check entries in reverse order (most recent first)
          for (let i = entries.length - 1; i >= 0; i--) {
            if (entries[i] && !entries[i].logoutTime) {
              // Directly update logoutTime in loginHistory object
              entries[i].logoutTime = logoutTimestamp;
              foundEntry = true;
              break;
            }
          }
          if (foundEntry) break;
        }
      }
    }
    
    // Directly update loginHistory in employee document
    await updateDoc(employeeRef, {
      lastLogoutTime: logoutTimestamp,
      isCurrentlyLoggedIn: false,
      loginHistory: loginHistory, // Directly store the updated loginHistory object
      updatedAt: serverTimestamp()
    });
    
    return true;
  } catch (error) {
    console.error('Error logging logout time:', error);
    throw error;
  }
};

/**
 * Check and log default logout time (6:30 PM) for employees who haven't logged out
 */
export const checkAndLogDefaultLogout = async () => {
  try {
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    
    // Check if current time is after 6:30 PM (18:30)
    const isAfter630PM = currentHour > 18 || (currentHour === 18 && currentMinute >= 30);
    
    if (isAfter630PM) {
      // Get all employees who are currently logged in
      const employeesRef = collection(db, COLLECTIONS.EMPLOYEES);
      const q = query(
        employeesRef,
        where('isCurrentlyLoggedIn', '==', true)
      );
      
      const querySnapshot = await getDocs(q);
      const today = new Date();
      today.setHours(18, 30, 0, 0); // Set to 6:30 PM today
      const defaultLogoutTime = Timestamp.fromDate(today);
      
      const updatePromises = [];
      querySnapshot.forEach((docSnap) => {
        const data = docSnap.data();
        let lastLoginTime;
        
        // Handle Firestore Timestamp
        if (data.lastLoginTime && typeof data.lastLoginTime.toDate === 'function') {
          lastLoginTime = data.lastLoginTime.toDate();
        } else if (data.lastLoginTime) {
          lastLoginTime = new Date(data.lastLoginTime);
        } else {
          return; // Skip if no login time
        }
        
        // Only update if login was today and before 6:30 PM
        const loginDate = new Date(lastLoginTime);
        const todayDate = new Date();
        const isSameDay = loginDate.toDateString() === todayDate.toDateString();
        const isBefore630PM = loginDate < today;
        const hasNoLogout = !data.lastLogoutTime;
        
        if (isSameDay && isBefore630PM && hasNoLogout) {
          const employeeRef = doc(db, COLLECTIONS.EMPLOYEES, docSnap.id);
          
          // Get or initialize loginHistory object
          let loginHistory = data.loginHistory || {};
          
          // Convert old array format to object format if needed
          if (Array.isArray(loginHistory)) {
            loginHistory = convertLoginHistoryToDateWise(loginHistory);
          }
          
          // Ensure loginHistory is an object
          if (typeof loginHistory !== 'object' || Array.isArray(loginHistory)) {
            loginHistory = {};
          }
          
          const today = new Date().toISOString().split('T')[0]; // Format: YYYY-MM-DD
          
          // Directly update the most recent login entry for today in loginHistory
          if (loginHistory[today] && Array.isArray(loginHistory[today]) && loginHistory[today].length > 0) {
            const todayEntries = loginHistory[today];
            // Find the last entry without logout time
            for (let i = todayEntries.length - 1; i >= 0; i--) {
              if (todayEntries[i] && !todayEntries[i].logoutTime) {
                // Directly update logoutTime in loginHistory object
                todayEntries[i].logoutTime = defaultLogoutTime;
                break;
              }
            }
          }
          
          // Directly update loginHistory in employee document
          updatePromises.push(
            updateDoc(employeeRef, {
              lastLogoutTime: defaultLogoutTime,
              isCurrentlyLoggedIn: false,
              loginHistory: loginHistory, // Directly store the updated loginHistory object
              updatedAt: serverTimestamp()
            })
          );
        }
      });
      
      if (updatePromises.length > 0) {
        await Promise.all(updatePromises);
      }
      return updatePromises.length;
    }
    
    return 0;
  } catch (error) {
    console.error('Error checking default logout:', error);
    return 0;
  }
};

/**
 * Get chat messages for an employee by extension number (one-time fetch)
 * Matches Firebase structure: { from, to, message, messageId, dateTime, timestamp, isRead }
 */
export const getChatMessages = async (extensionNumber) => {
  try {
    if (!extensionNumber) {
      console.warn('Extension number is required to fetch chat messages');
      return [];
    }

    const chatDocRef = doc(db, COLLECTIONS.CHAT_DATA, extensionNumber);
    const chatDoc = await getDoc(chatDocRef);
    
    if (chatDoc.exists()) {
      const data = chatDoc.data();
      const messages = data.messages || [];
      
      // Convert Firestore timestamps to JavaScript dates
      return messages.map(msg => {
        const timestamp = msg.timestamp?.toDate ? msg.timestamp.toDate() : 
                         (msg.dateTime?.toDate ? msg.dateTime.toDate() : 
                         (msg.timestamp || msg.dateTime || new Date()));
        
        return {
          ...msg,
          timestamp: timestamp,
          dateTime: timestamp
        };
      }).sort((a, b) => {
        // Sort by timestamp ascending (oldest first)
        const dateA = a.timestamp instanceof Date ? a.timestamp : new Date(a.timestamp);
        const dateB = b.timestamp instanceof Date ? b.timestamp : new Date(b.timestamp);
        return dateA - dateB;
      });
    }
    
    return [];
  } catch (error) {
    console.error('Error fetching chat messages:', error);
    return [];
  }
};

/**
 * Subscribe to real-time chat messages for an employee by extension number
 * Returns an unsubscribe function to clean up the listener
 * Matches Firebase structure: { from, to, message, messageId, dateTime, timestamp, isRead }
 */
export const subscribeToChatMessages = (extensionNumber, callback) => {
  if (!extensionNumber) {
    console.warn('Extension number is required to subscribe to chat messages');
    return () => {}; // Return empty unsubscribe function
  }

  const chatDocRef = doc(db, COLLECTIONS.CHAT_DATA, extensionNumber);
  
  // Set up real-time listener
  const unsubscribe = onSnapshot(
    chatDocRef,
    (docSnapshot) => {
      if (docSnapshot.exists()) {
        const data = docSnapshot.data();
        const messages = data.messages || [];
        
        // Convert Firestore timestamps to JavaScript dates
        const processedMessages = messages.map(msg => {
          const timestamp = msg.timestamp?.toDate ? msg.timestamp.toDate() : 
                           (msg.dateTime?.toDate ? msg.dateTime.toDate() : 
                           (msg.timestamp || msg.dateTime || new Date()));
          
          return {
            ...msg,
            timestamp: timestamp,
            dateTime: timestamp
          };
        }).sort((a, b) => {
          // Sort by timestamp ascending (oldest first)
          const dateA = a.timestamp instanceof Date ? a.timestamp : new Date(a.timestamp);
          const dateB = b.timestamp instanceof Date ? b.timestamp : new Date(b.timestamp);
          return dateA - dateB;
        });
        
        callback(processedMessages);
      } else {
        // Document doesn't exist yet, return empty array
        callback([]);
      }
    },
    (error) => {
      console.error('Error in chat messages subscription:', error);
      callback([]); // Return empty array on error
    }
  );
  
  return unsubscribe;
};

/**
 * Save a chat message to the ChatData collection
 * Matches Firebase structure: { from, to, message, messageId, dateTime, timestamp, isRead }
 */
export const saveChatMessage = async (extensionNumber, messageText, to = 'admin') => {
  try {
    if (!extensionNumber) {
      throw new Error('Extension number is required to save chat message');
    }

    const chatDocRef = doc(db, COLLECTIONS.CHAT_DATA, extensionNumber);
    const chatDoc = await getDoc(chatDocRef);
    
    // Generate unique messageId
    const messageId = `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    // Use Timestamp.now() instead of serverTimestamp() for arrayUnion compatibility
    const now = Timestamp.now();
    
    const message = {
      from: extensionNumber,
      to: to,
      message: messageText,
      messageId: messageId,
      dateTime: now,
      timestamp: now,
      isRead: false
    };
    
    if (chatDoc.exists()) {
      // Document exists, add message to existing messages array
      await updateDoc(chatDocRef, {
        messages: arrayUnion(message),
        updatedAt: serverTimestamp()
      });
    } else {
      // Document doesn't exist, create it with the first message
      await setDoc(chatDocRef, {
        extensionNumber: extensionNumber,
        messages: [message],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    }
    
    return {
      ...message,
      timestamp: new Date(), // Return current date for immediate UI update
      dateTime: new Date()
    };
  } catch (error) {
    console.error('Error saving chat message:', error);
    throw error;
  }
};

/**
 * Mark all unread messages as read for a specific extension number
 * @param {string} extensionNumber - The extension number
 */
export const markChatMessagesAsRead = async (extensionNumber) => {
  try {
    if (!extensionNumber) {
      throw new Error('Extension number is required to mark messages as read');
    }

    const chatDocRef = doc(db, COLLECTIONS.CHAT_DATA, extensionNumber);
    const chatDoc = await getDoc(chatDocRef);
    
    if (!chatDoc.exists()) {
      return; // No messages to mark as read
    }

    const data = chatDoc.data();
    const messages = data.messages || [];
    
    // Mark all unread messages as read
    const updatedMessages = messages.map(msg => ({
      ...msg,
      isRead: true
    }));
    
    await updateDoc(chatDocRef, {
      messages: updatedMessages,
      updatedAt: serverTimestamp()
    });
    
    return true;
  } catch (error) {
    console.error('Error marking messages as read:', error);
    throw error;
  }
};

/**
 * Update chat status (online or last seen timestamp)
 * @param {string} extensionNumber - The extension number
 * @param {string} status - Status: 'online' or 'offline'
 * @param {Date|Timestamp} lastSeen - Last seen timestamp (optional, only for offline)
 */
export const updateChatStatus = async (extensionNumber, status, lastSeen = null) => {
  try {
    if (!extensionNumber) {
      throw new Error('Extension number is required to update chat status');
    }

    const chatDocRef = doc(db, COLLECTIONS.CHAT_DATA, extensionNumber);
    const chatDoc = await getDoc(chatDocRef);
    
    const updateData = {
      status: status,
      updatedAt: serverTimestamp()
    };

    if (status === 'offline' && lastSeen) {
      // Convert Date to Timestamp if needed
      const lastSeenTimestamp = lastSeen instanceof Date 
        ? Timestamp.fromDate(lastSeen) 
        : (lastSeen instanceof Timestamp ? lastSeen : Timestamp.now());
      updateData.lastSeen = lastSeenTimestamp;
    }
    
    if (chatDoc.exists()) {
      // For existing documents, use updateDoc
      if (status === 'online') {
        // Remove lastSeen field when online using deleteField()
        updateData.lastSeen = deleteField();
      }
      await updateDoc(chatDocRef, updateData);
    } else {
      // Create document if it doesn't exist
      // Don't include lastSeen if status is online (don't use deleteField in setDoc)
      const newDocData = {
        extensionNumber: extensionNumber,
        messages: [],
        status: status,
        updatedAt: serverTimestamp(),
        createdAt: serverTimestamp()
      };
      
      // Only add lastSeen if status is offline and lastSeen is provided
      if (status === 'offline' && lastSeen) {
        const lastSeenTimestamp = lastSeen instanceof Date 
          ? Timestamp.fromDate(lastSeen) 
          : (lastSeen instanceof Timestamp ? lastSeen : Timestamp.now());
        newDocData.lastSeen = lastSeenTimestamp;
      }
      
      await setDoc(chatDocRef, newDocData);
    }
    
    return true;
  } catch (error) {
    console.error('Error updating chat status:', error);
    throw error;
  }
};

/**
 * Subscribe to chat status changes for an extension number
 * @param {string} extensionNumber - The extension number
 * @param {function} callback - Callback function that receives status data
 * @returns {function} Unsubscribe function
 */
export const subscribeToChatStatus = (extensionNumber, callback) => {
  if (!extensionNumber) {
    console.warn('Extension number is required to subscribe to chat status');
    return () => {}; // Return empty unsubscribe function
  }

  const chatDocRef = doc(db, COLLECTIONS.CHAT_DATA, extensionNumber);
  
  // Set up real-time listener
  const unsubscribe = onSnapshot(
    chatDocRef,
    (docSnapshot) => {
      if (docSnapshot.exists()) {
        const data = docSnapshot.data();
        const status = data.status || 'offline';
        // Check if lastSeen field exists and is not null/undefined
        let lastSeen = null;
        if (data.lastSeen !== undefined && data.lastSeen !== null) {
          lastSeen = data.lastSeen?.toDate ? data.lastSeen.toDate() : 
                     (data.lastSeen instanceof Date ? data.lastSeen : null);
        }
        
        callback({
          status: status,
          lastSeen: lastSeen,
          updatedAt: data.updatedAt
        });
      } else {
        // Document doesn't exist yet, return default status
        callback({
          status: 'offline',
          lastSeen: null,
          updatedAt: null
        });
      }
    },
    (error) => {
      console.error('Error in chat status subscription:', error);
      callback({
        status: 'offline',
        lastSeen: null,
        updatedAt: null
      });
    }
  );
  
  return unsubscribe;
};

/**
 * Format time for chat display
 */
const formatChatTime = (date) => {
  if (!date) return '';
  const now = new Date();
  const chatDate = date instanceof Date ? date : new Date(date);
  const diffTime = now - chatDate;
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) {
    return chatDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } else if (diffDays === 1) {
    return 'Yesterday';
  } else if (diffDays < 7) {
    return `${diffDays} days ago`;
  } else {
    return chatDate.toLocaleDateString();
  }
};

/**
 * Fetch all team chats from ChatData collection
 * @param {string} currentUserId - The current user's ID to exclude their messages from unread count
 */
export const fetchTeamChats = async (currentUserId = 'admin') => {
  try {
    const chatDataRef = collection(db, COLLECTIONS.CHAT_DATA);
    const querySnapshot = await getDocs(chatDataRef);
    
    const chats = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      const messages = data.messages || [];
      const lastMessage = messages.length > 0 ? messages[messages.length - 1] : null;
      
      const chatName = data.chatName || data.name || data.extensionNumber || doc.id;
      
      const unreadCount = messages.filter(msg => {
        if (msg.from === currentUserId || msg.from === 'admin') {
          return false;
        }
        return !msg.isRead;
      }).length;
      
      chats.push({
        id: doc.id,
        name: chatName,
        lastMessage: lastMessage?.message || lastMessage?.text || 'No messages yet',
        time: lastMessage?.timestamp?.toDate ? 
          formatChatTime(lastMessage.timestamp.toDate()) : 
          (lastMessage?.dateTime?.toDate ? formatChatTime(lastMessage.dateTime.toDate()) : ''),
        unread: unreadCount,
        avatar: chatName ? chatName.substring(0, 2).toUpperCase() : 'TC',
        online: data.status === 'online',
        participants: data.participants || [],
        createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(),
        updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : new Date()
      });
    });
    
    chats.sort((a, b) => {
      const timeA = a.updatedAt instanceof Date ? a.updatedAt.getTime() : 0;
      const timeB = b.updatedAt instanceof Date ? b.updatedAt.getTime() : 0;
      return timeB - timeA;
    });
    
    return chats;
  } catch (error) {
    console.error('Error fetching team chats:', error);
    return [];
  }
};

/**
 * Subscribe to real-time updates for all team chats
 */
export const subscribeToTeamChats = (currentUserId = 'admin', callback) => {
  if (!currentUserId) {
    console.warn('Current user ID is required to subscribe to team chats');
    return () => {};
  }

  const chatDataRef = collection(db, COLLECTIONS.CHAT_DATA);
  
  const unsubscribe = onSnapshot(
    chatDataRef,
    (querySnapshot) => {
      const chats = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        const messages = data.messages || [];
        const lastMessage = messages.length > 0 ? messages[messages.length - 1] : null;
        
        const chatName = data.chatName || data.name || data.extensionNumber || doc.id;
        
        const unreadCount = messages.filter(msg => {
          if (msg.from === currentUserId || msg.from === 'admin') {
            return false;
          }
          return !msg.isRead;
        }).length;
        
        chats.push({
          id: doc.id,
          name: chatName,
          lastMessage: lastMessage?.message || lastMessage?.text || 'No messages yet',
          time: lastMessage?.timestamp?.toDate ? 
            formatChatTime(lastMessage.timestamp.toDate()) : 
            (lastMessage?.dateTime?.toDate ? formatChatTime(lastMessage.dateTime.toDate()) : ''),
          unread: unreadCount,
          avatar: chatName ? chatName.substring(0, 2).toUpperCase() : 'TC',
          online: data.status === 'online',
          participants: data.participants || [],
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(),
          updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : new Date()
        });
      });
      
      chats.sort((a, b) => {
        const timeA = a.updatedAt instanceof Date ? a.updatedAt.getTime() : 0;
        const timeB = b.updatedAt instanceof Date ? b.updatedAt.getTime() : 0;
        return timeB - timeA;
      });
      
      callback(chats);
    },
    (error) => {
      console.error('Error in team chats subscription:', error);
      callback([]);
    }
  );
  
  return unsubscribe;
};

/**
 * Subscribe to real-time team chat messages
 */
export const subscribeToTeamChatMessages = (chatId, callback) => {
  if (!chatId) {
    console.warn('Chat ID is required to subscribe to chat messages');
    return () => {};
  }

  const chatDocRef = doc(db, COLLECTIONS.CHAT_DATA, chatId);
  
  const unsubscribe = onSnapshot(
    chatDocRef,
    (docSnapshot) => {
      if (docSnapshot.exists()) {
        const data = docSnapshot.data();
        const messages = data.messages || [];
        
        const processedMessages = messages.map(msg => {
          const timestamp = msg.timestamp?.toDate ? msg.timestamp.toDate() : 
                           (msg.dateTime?.toDate ? msg.dateTime.toDate() : 
                           (msg.timestamp || msg.dateTime || new Date()));
          
          return {
            ...msg,
            timestamp: timestamp,
            dateTime: timestamp,
            id: msg.messageId || msg.id || `msg_${Date.now()}_${Math.random()}`,
            sender: msg.from === 'admin' ? 'me' : 'other',
            senderName: msg.fromName || msg.from || 'Unknown',
            text: msg.message || msg.text || '',
            time: timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
          };
        }).sort((a, b) => {
          const dateA = a.timestamp instanceof Date ? a.timestamp : new Date(a.timestamp);
          const dateB = b.timestamp instanceof Date ? b.timestamp : new Date(b.timestamp);
          return dateA - dateB;
        });
        
        callback(processedMessages);
      } else {
        callback([]);
      }
    },
    (error) => {
      console.error('Error in team chat messages subscription:', error);
      callback([]);
    }
  );
  
  return unsubscribe;
};

/**
 * Send a message to a team chat
 */
export const sendTeamChatMessage = async (chatId, messageText, senderId = 'admin', senderName = 'Admin') => {
  try {
    if (!chatId || !messageText) {
      throw new Error('Chat ID and message text are required');
    }

    const chatDocRef = doc(db, COLLECTIONS.CHAT_DATA, chatId);
    const chatDoc = await getDoc(chatDocRef);
    
    const messageId = `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const now = Timestamp.now();
    
    const message = {
      from: senderId,
      fromName: senderName,
      to: 'team',
      message: messageText,
      messageId: messageId,
      dateTime: now,
      timestamp: now,
      isRead: true
    };
    
    if (chatDoc.exists()) {
      await updateDoc(chatDocRef, {
        messages: arrayUnion(message),
        updatedAt: serverTimestamp()
      });
    } else {
      await setDoc(chatDocRef, {
        chatName: chatId,
        messages: [message],
        participants: [senderId],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    }
    
    return {
      ...message,
      timestamp: new Date(),
      dateTime: new Date(),
      id: messageId
    };
  } catch (error) {
    console.error('Error sending team chat message:', error);
    throw error;
  }
};

/**
 * Mark messages as read in a team chat
 */
export const markTeamChatMessagesAsRead = async (chatId, userId = 'admin') => {
  try {
    if (!chatId) {
      throw new Error('Chat ID is required');
    }

    const chatDocRef = doc(db, COLLECTIONS.CHAT_DATA, chatId);
    const chatDoc = await getDoc(chatDocRef);
    
    if (!chatDoc.exists()) {
      return;
    }
    
    const data = chatDoc.data();
    const messages = data.messages || [];
    
    const updatedMessages = messages.map(msg => {
      if (!msg.isRead && msg.from !== userId) {
        return {
          ...msg,
          isRead: true
        };
      }
      return msg;
    });
    
    const hasUnread = messages.some(msg => !msg.isRead && msg.from !== userId);
    if (hasUnread) {
      await updateDoc(chatDocRef, {
        messages: updatedMessages,
        updatedAt: serverTimestamp()
      });
    }
  } catch (error) {
    console.error('Error marking messages as read:', error);
    throw error;
  }
};

// ==================== CHALLENGE MODE OPERATIONS ====================

/**
 * Fetch all challenges from challengeMode collection
 */
export const fetchChallenges = async () => {
  try {
    const challengeModeRef = collection(db, COLLECTIONS.CHALLENGE_MODE);
    const querySnapshot = await getDocs(challengeModeRef);
    
    const challenges = [];
    
    querySnapshot.forEach((doc) => {
      const challengeName = doc.id;
      const challengeData = doc.data();
      
      // Get settings if they exist
      const settings = challengeData.settings || { limitperDay: 0, asignTo: [], category: '' };
      
      // Get all phone numbers in this challenge (exclude settings)
      const phoneNumbers = Object.keys(challengeData).filter(key => 
        key !== 'settings' && challengeData[key] && typeof challengeData[key] === 'object' && challengeData[key].date
      );
      
      if (phoneNumbers.length > 0) {
        // Count taken and available phones
        const takenPhones = phoneNumbers.filter(phone => 
          challengeData[phone].isTaken === true
        ).length;
        
        const availablePhones = phoneNumbers.length - takenPhones;
        
        // Get premium status from first phone (all phones in same challenge should have same isPremium)
        const isPremium = phoneNumbers.length > 0 ? challengeData[phoneNumbers[0]].isPremium : false;
        
        challenges.push({
          id: challengeName,
          name: challengeName,
          isPremium: isPremium,
          phoneCount: phoneNumbers.length,
          totalPhones: phoneNumbers.length,
          takenPhones: takenPhones,
          availablePhones: availablePhones,
          phoneNumbers: phoneNumbers,
          challengeData: challengeData,
          settings: settings,
          createdAt: phoneNumbers.length > 0 && challengeData[phoneNumbers[0]].date 
            ? challengeData[phoneNumbers[0]].date 
            : formatDate(new Date())
        });
      }
    });
    
    return challenges;
  } catch (error) {
    console.error('Error fetching challenges:', error);
    throw error;
  }
};

/**
 * Fetch a specific challenge by name
 */
export const fetchChallengeByName = async (challengeName) => {
  try {
    const challengeRef = doc(db, COLLECTIONS.CHALLENGE_MODE, challengeName);
    const docSnap = await getDoc(challengeRef);
    
    if (docSnap.exists()) {
      const challengeData = docSnap.data();
      const settings = challengeData.settings || { limitperDay: 0, asignTo: [], category: '' };
      const phoneNumbers = Object.keys(challengeData).filter(key => 
        key !== 'settings' && challengeData[key] && typeof challengeData[key] === 'object' && challengeData[key].date
      );
      
      return {
        id: challengeName,
        name: challengeName,
        phoneNumbers: phoneNumbers,
        challengeData: challengeData,
        settings: settings
      };
    }
    
    return null;
  } catch (error) {
    console.error('Error fetching challenge by name:', error);
    throw error;
  }
};

/**
 * Take/claim a phone number from a challenge
 */
export const takeChallengePhone = async (challengeName, phoneNumber, employeeName, employeeId) => {
  try {
    const challengeRef = doc(db, COLLECTIONS.CHALLENGE_MODE, challengeName);
    const docSnap = await getDoc(challengeRef);
    
    if (!docSnap.exists()) {
      throw new Error('Challenge not found');
    }
    
    const challengeData = docSnap.data();
    const phoneData = challengeData[phoneNumber];
    
    if (!phoneData) {
      throw new Error('Phone number not found in challenge');
    }
    
    if (phoneData.isTaken) {
      throw new Error('This phone number is already taken');
    }
    
    // Update the phone number to be taken
    await updateDoc(challengeRef, {
      [phoneNumber]: {
        ...phoneData,
        isTaken: true,
        takenBy: employeeName,
        takenById: employeeId,
        takenAt: serverTimestamp()
      }
    });
    
    return { success: true };
  } catch (error) {
    console.error('Error taking challenge phone:', error);
    throw error;
  }
};

/**
 * Update challenge phone status and remarks
 */
export const updateChallengePhone = async (challengeName, phoneNumber, updateData) => {
  try {
    const challengeRef = doc(db, COLLECTIONS.CHALLENGE_MODE, challengeName);
    const docSnap = await getDoc(challengeRef);
    
    if (!docSnap.exists()) {
      throw new Error('Challenge not found');
    }
    
    const challengeData = docSnap.data();
    const phoneData = challengeData[phoneNumber];
    
    if (!phoneData) {
      throw new Error('Phone number not found in challenge');
    }
    
    // Update the phone number data
    await updateDoc(challengeRef, {
      [phoneNumber]: {
        ...phoneData,
        ...updateData,
        updatedAt: serverTimestamp()
      }
    });
    
    return { success: true };
  } catch (error) {
    console.error('Error updating challenge phone:', error);
    throw error;
  }
};

/**
 * Automatically assign multiple phone numbers to an employee
 */
export const autoAssignChallengePhones = async (challengeName, phoneNumbers, employeeName, employeeId) => {
  try {
    const challengeRef = doc(db, COLLECTIONS.CHALLENGE_MODE, challengeName);
    const docSnap = await getDoc(challengeRef);
    
    if (!docSnap.exists()) {
      throw new Error('Challenge not found');
    }
    
    const challengeData = docSnap.data();
    const updateData = {};
    let assignedCount = 0;
    
    // Prepare updates for each phone number
    phoneNumbers.forEach(phoneNumber => {
      const phoneData = challengeData[phoneNumber];
      if (phoneData && !phoneData.isTaken) {
        updateData[phoneNumber] = {
          ...phoneData,
          isTaken: true,
          takenBy: employeeName,
          takenById: employeeId,
          takenAt: serverTimestamp()
        };
        assignedCount++;
      }
    });
    
    // Batch update all phones at once
    if (Object.keys(updateData).length > 0) {
      await updateDoc(challengeRef, updateData);
    }
    
    return { success: true, assignedCount };
  } catch (error) {
    console.error('Error auto-assigning challenge phones:', error);
    throw error;
  }
};

/**
 * Subscribe to real-time updates for a specific challenge
 */
export const subscribeToChallenge = (challengeName, callback) => {
  try {
    const challengeRef = doc(db, COLLECTIONS.CHALLENGE_MODE, challengeName);
    
    const unsubscribe = onSnapshot(challengeRef, (docSnap) => {
      if (docSnap.exists()) {
        const challengeData = docSnap.data();
        const settings = challengeData.settings || { limitperDay: 0, asignTo: [], category: '' };
        const phoneNumbers = Object.keys(challengeData).filter(key => 
          key !== 'settings' && challengeData[key] && typeof challengeData[key] === 'object' && challengeData[key].date
        );
        
        callback({
          id: challengeName,
          name: challengeName,
          phoneNumbers: phoneNumbers,
          challengeData: challengeData,
          settings: settings
        });
      } else {
        callback(null);
      }
    }, (error) => {
      console.error('Error in challenge subscription:', error);
      callback(null);
    });
    
    return unsubscribe;
  } catch (error) {
    console.error('Error setting up challenge subscription:', error);
    return () => {}; // Return empty unsubscribe function
  }
};

/**
 * Subscribe to real-time updates for all challenges
 */
export const subscribeToChallenges = (callback) => {
  try {
    const challengeModeRef = collection(db, COLLECTIONS.CHALLENGE_MODE);
    
    const unsubscribe = onSnapshot(challengeModeRef, (querySnapshot) => {
      const challenges = [];
      
      querySnapshot.forEach((doc) => {
        const challengeName = doc.id;
        const challengeData = doc.data();
        
        // Get settings if they exist
        const settings = challengeData.settings || { limitperDay: 0, asignTo: [], category: '' };
        
        // Get all phone numbers in this challenge (exclude settings)
        const phoneNumbers = Object.keys(challengeData).filter(key => 
          key !== 'settings' && challengeData[key] && typeof challengeData[key] === 'object' && challengeData[key].date
        );
        
        if (phoneNumbers.length > 0) {
          // Count taken and available phones
          const takenPhones = phoneNumbers.filter(phone => 
            challengeData[phone].isTaken === true
          ).length;
          
          const availablePhones = phoneNumbers.length - takenPhones;
          
          // Get premium status from first phone
          const isPremium = phoneNumbers.length > 0 ? challengeData[phoneNumbers[0]].isPremium : false;
          
          challenges.push({
            id: challengeName,
            name: challengeName,
            isPremium: isPremium,
            phoneCount: phoneNumbers.length,
            totalPhones: phoneNumbers.length,
            takenPhones: takenPhones,
            availablePhones: availablePhones,
            phoneNumbers: phoneNumbers,
            challengeData: challengeData,
            settings: settings,
            createdAt: phoneNumbers.length > 0 && challengeData[phoneNumbers[0]].date 
              ? challengeData[phoneNumbers[0]].date 
              : formatDate(new Date())
          });
        }
      });
      
      callback(challenges);
    }, (error) => {
      console.error('Error in challenges subscription:', error);
      callback([]);
    });
    
    return unsubscribe;
  } catch (error) {
    console.error('Error setting up challenges subscription:', error);
    return () => {}; // Return empty unsubscribe function
  }
};

